// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: turning an own dataset's id into something the loader
 * plays. Opens a token session (`privateData.ts`), reads the release's
 * thumbnail and colorbar with the token into object URLs, and registers
 * the dataset in `dataService`'s private list (never the catalogue). Used
 * by the My data tab's Open, and by the loader for a link or a reload that
 * names an own dataset by id.
 */

import { t } from '../i18n'
import type { Dataset } from '../types'
import { dataService } from './dataService'
import { onAccountChange } from './account'
import { authorisedFetch, closeAllPrivateSessions, closePrivateSession, hasSession, openPrivateSession, ownDatasetTitle, privateObjectUrl } from './privateData'

const rec = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null

async function json(url: string): Promise<Record<string, unknown> | null> {
  try {
    const res = await authorisedFetch(url, { headers: { Accept: 'application/json' }, cache: 'no-cache' })
    return res.ok ? rec(await res.json()) : null
  } catch {
    return null
  }
}

/** The release's thumbnail and colorbar addresses (`presentation`), resolved against `release.json`. */
async function presentationOf(latest: string): Promise<{ thumbnail: string | null; colorbar: string | null }> {
  const pointer = await json(latest)
  const releasePath = rec(pointer?.releaseDescriptor)?.url
  if (typeof releasePath !== 'string') return { thumbnail: null, colorbar: null }
  const releaseUrl = new URL(releasePath, latest).toString()
  const release = await json(releaseUrl)
  const pres = rec(release?.presentation)
  const pick = (key: string): string | null => {
    const url = rec(pres?.[key])?.url
    return typeof url === 'string' && url ? new URL(url, releaseUrl).toString() : null
  }
  return { thumbnail: pick('thumbnail'), colorbar: pick('colorbar') }
}

/**
 * Make an own dataset playable: a session and a registered `Dataset`.
 * Answers the dataset, or the route's error code (`not_ready`, `not_found`,
 * `plan_required`, ...).
 */
export async function ensureOwnDataset(id: string, title?: string): Promise<{ ok: true; dataset: Dataset } | { ok: false; error: string }> {
  const known = dataService.getDatasetById(id)
  if (known?.ownData && hasSession(id)) return { ok: true, dataset: known }
  const opened = await openPrivateSession(id)
  if (!opened.ok) {
    dataService.forgetPrivateDataset(id)
    return { ok: false, error: opened.error }
  }
  let name = title ?? (known?.ownData ? known.title : undefined)
  if (!name) name = (await ownDatasetTitle(id))?.title ?? t('myData.untitled')
  const pictures = await presentationOf(opened.value.latest)
  const [thumbnailLink, legendLink] = await Promise.all([
    pictures.thumbnail ? privateObjectUrl(id, pictures.thumbnail) : Promise.resolve(null),
    pictures.colorbar ? privateObjectUrl(id, pictures.colorbar) : Promise.resolve(null),
  ])
  const dataset: Dataset = {
    id,
    title: name,
    format: 'application/dash+xml',
    // The loader follows the pointer to the release's MPD, .dsa and encoding at every load.
    dataLink: opened.value.latest,
    releaseDescriptorLink: opened.value.latest,
    organization: t('myData.source'),
    ...(thumbnailLink ? { thumbnailLink } : {}),
    ...(legendLink ? { legendLink } : {}),
    tags: [],
    defaultBordersVisible: true,
    ownData: true,
  }
  dataService.registerPrivateDataset(dataset)
  return { ok: true, dataset }
}

/** Forget an own dataset in this browser: its session, token and pictures (deleted, or signed out). */
export function forgetOwnDataset(id: string): void {
  closePrivateSession(id)
  dataService.forgetPrivateDataset(id)
}

// Signing out ends every session in this browser at once. Only a real
// sign-out does: a guest in a meeting (never signed in) keeps the sessions
// the meeting gave it when the account check first says "signed out".
let wasSignedIn = false
onAccountChange((state) => {
  if (state.status === 'signedIn') wasSignedIn = true
  if (state.status === 'signedOut' && wasSignedIn) {
    wasSignedIn = false
    closeAllPrivateSessions()
  }
})
